import { useContext, useEffect, useMemo, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import axios from "axios";
import {
    Add,
    Chart,
    Coin1,
    DocumentDownload,
    Eye,
    LampOn,
    Messages,
    Profile2User,
    SearchNormal1,
    Share,
} from "iconsax-react";
import { useTranslation } from "react-i18next";
import CustomerClubTableRow from "./TableRow";
import NewUserModal from "./NewUserModal";
import MemberReportModal from "./MemberReportModal";
import MemberDetailModal from "./MemberDetailModal";
import MessagesModal from "../../../messages/MessagesModal";
import { MessageContext } from "../../../messages/MessageContext";
import DBButton from "../../../../component/global/DBButton";
import DBPagination from "../../../../component/global/DBPagination";
import { ToastContext } from "../../../../component/global/ToastContext";
import { get } from "../../../../controlers/global/ConfigRequest";
import {
    COMPANY_CLUBS_BASE,
    COMPANY_CLUBS_EXPORT,
    COMPANY_CLUBS_SUMMARY,
    SETTING_FORMS_USER_GROUP,
} from "../../../../helperData/ApiAddress";
import {
    systemUserGroupRequestParams,
    normalizeSystemUserGroups,
} from "../../../../helperData/systemUserGroups";
import { clubMemberQuery, formatCount, downloadBlob } from "./helpers";
import "./style.scss";

const PAGE_SIZE = 20;

const SUMMARY_METRICS = [
    {
        key: "members",
        icon: Profile2User,
        label: "Organizational.CustomerClub.Members.Metric.Members",
    },
    {
        key: "messages",
        icon: Messages,
        label: "Organizational.CustomerClub.Members.Metric.Messages",
        hint: "Organizational.CustomerClub.Members.Hint.Messages",
    },
    {
        key: "ideas",
        icon: LampOn,
        label: "Organizational.CustomerClub.Members.Metric.Ideas",
        hint: "Organizational.CustomerClub.Members.Hint.Ideas",
    },
    {
        key: "referral_opens",
        icon: Share,
        label: "Organizational.CustomerClub.Members.Metric.Referrals",
        hint: "Organizational.CustomerClub.Members.Hint.Referrals",
    },
    {
        key: "coins_earned",
        icon: Coin1,
        label: "Organizational.CustomerClub.Members.Metric.Coins",
        hint: "Organizational.CustomerClub.Members.Hint.Coins",
    },
    {
        key: "content_views",
        icon: Eye,
        label: "Organizational.CustomerClub.Members.Metric.Views",
        hint: "Organizational.CustomerClub.Members.Hint.Views",
    },
];

export default function UserManagement({ activeTab }) {
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [userGroupFilter, setUserGroupFilter] = useState("");
    const [userModal, setUserModal] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [exportLoading, setExportLoading] = useState(false);
    const [members, setMembers] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [refreshList, setRefreshList] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [itemId, setItemId] = useState();
    const [userGroups, setUserGroups] = useState([]);
    const [summary, setSummary] = useState(null);
    const [error, setError] = useState("");
    const [openMember, setOpenMember] = useState(null);
    const [messageModal, setMessageModal] = useState(false);
    const [feedbackId, setFeedbackId] = useState();
    const [refreshMode, setRefreshMode] = useState(false);
    const { t, i18n } = useTranslation();
    const toast = useContext(ToastContext);
    const locale = i18n.language === "en" ? "en-US" : "fa-IR";
    const filters = useMemo(
        () => clubMemberQuery(search, userGroupFilter),
        [search, userGroupFilter]
    );

    const changeRefreshValue = () => {
        setRefreshList((value) => !value);
    };

    const showNewUserModal = () => {
        setUserModal((open) => !open);
        setEditMode(false);
        setItemId(undefined);
    };

    const fetchMembers = (page = currentPage) => {
        setLoading(true);
        setError("");
        get(COMPANY_CLUBS_BASE, { page, ...filters })
            .then((response) => {
                setMembers(response.results || []);
                setTotalCount(response.count || 0);
            })
            .catch(() => {
                setMembers([]);
                setTotalCount(0);
                setError(t("Organizational.CustomerClub.Members.LoadError"));
            })
            .finally(() => setLoading(false));
    };

    const fetchSummary = () => {
        setSummaryLoading(true);
        get(COMPANY_CLUBS_SUMMARY, filters)
            .then((response) => setSummary(response))
            .catch(() => setSummary(null))
            .finally(() => setSummaryLoading(false));
    };

    const exportCsv = () => {
        setExportLoading(true);
        axios
            .get(COMPANY_CLUBS_EXPORT, {
                params: filters,
                responseType: "blob",
                headers: {
                    Authorization: `Bearer ${window.sessionStorage.auth}`,
                },
            })
            .then((response) => {
                const blob = new Blob([response.data], {
                    type: "text/csv;charset=utf-8;",
                });
                downloadBlob(blob, "club-members.csv");
                toast(
                    t("Organizational.CustomerClub.Members.Csv.Success"),
                    "success"
                );
            })
            .catch(() => {
                toast(
                    t("Organizational.CustomerClub.Members.Csv.Error"),
                    "danger"
                );
            })
            .finally(() => setExportLoading(false));
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearch((prev) => {
                const next = searchInput.trim();
                if (prev !== next) {
                    setCurrentPage(1);
                }
                return next;
            });
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput]);

    useEffect(() => {
        if (activeTab && activeTab !== "1") return;
        fetchMembers(currentPage);
        fetchSummary();
    }, [refreshList, currentPage, search, userGroupFilter, activeTab]);

    useEffect(() => {
        get(SETTING_FORMS_USER_GROUP, systemUserGroupRequestParams()).then(
            (response) => {
                setUserGroups(normalizeSystemUserGroups(response));
            }
        );
    }, []);

    const hasMembers = members.length > 0;
    const showEmpty = !loading && !error && !hasMembers;

    return (
        <div className="member-management">
            <header className="member-toolbar">
                <div className="member-toolbar-copy">
                    <h2 tabIndex={0}>
                        {t("Organizational.CustomerClub.Members.Title")}
                        <span className="member-title-tip" role="tooltip">
                            {t("Organizational.CustomerClub.Members.Subtitle")}
                        </span>
                    </h2>
                    <p>{t("Organizational.CustomerClub.Members.Subtitle")}</p>
                </div>
                <div className="member-toolbar-actions">
                    <DBButton
                        type="secondary"
                        outlined
                        className="member-toolbar-btn"
                        aria-label={t("Organizational.CustomerClub.Members.Report")}
                        title={t("Organizational.CustomerClub.Members.Report")}
                        onClick={() => setReportOpen(true)}
                        caption={
                            <>
                                <Chart size={18} aria-hidden="true" />
                                <span className="member-toolbar-label">
                                    {t("Organizational.CustomerClub.Members.Report")}
                                </span>
                            </>
                        }
                    />
                    <DBButton
                        type="secondary"
                        outlined
                        className="member-toolbar-btn"
                        aria-label={t("Organizational.CustomerClub.Members.Csv")}
                        title={t("Organizational.CustomerClub.Members.Csv")}
                        loading={exportLoading}
                        onClick={exportCsv}
                        caption={
                            <>
                                <DocumentDownload size={18} aria-hidden="true" />
                                <span className="member-toolbar-label">
                                    {t("Organizational.CustomerClub.Members.Csv")}
                                </span>
                            </>
                        }
                    />
                    <DBButton
                        type="primary"
                        className="member-toolbar-btn"
                        aria-label={t("Organizational.CustomerClub.Members.Add")}
                        title={t("Organizational.CustomerClub.Members.Add")}
                        onClick={showNewUserModal}
                        caption={
                            <>
                                <Add size={18} aria-hidden="true" />
                                <span className="member-toolbar-label">
                                    {t("Organizational.CustomerClub.Members.Add")}
                                </span>
                            </>
                        }
                    />
                </div>
            </header>

            <section className="member-summary" aria-label={t("Organizational.CustomerClub.Members.Report")}>
                {SUMMARY_METRICS.map((metric) => {
                    const Icon = metric.icon;
                    return (
                        <div
                            key={metric.key}
                            title={
                                metric.hint ? t(metric.hint) : t(metric.label)
                            }
                        >
                            <Icon size={16} aria-hidden="true" />
                            <div>
                                <span>{t(metric.label)}</span>
                                <strong className="tabular-nums">
                                    {summaryLoading
                                        ? "—"
                                        : formatCount(
                                              summary?.[metric.key],
                                              locale
                                          )}
                                </strong>
                            </div>
                        </div>
                    );
                })}
            </section>

            <div className="member-filters">
                <label className="member-search" htmlFor="member-search-input">
                    <span className="visually-hidden">
                        {t("Organizational.CustomerClub.Members.Search.Label")}
                    </span>
                    <SearchNormal1 size={18} aria-hidden="true" />
                    <input
                        id="member-search-input"
                        value={searchInput}
                        onChange={(event) => setSearchInput(event.target.value)}
                        placeholder={t(
                            "Organizational.CustomerClub.Members.Search.Placeholder"
                        )}
                    />
                </label>
                <label className="member-group-filter" htmlFor="member-group-filter">
                    <span className="visually-hidden">
                        {t("Organizational.CustomerClub.Member.UserGroup.Label")}
                    </span>
                    <select
                        id="member-group-filter"
                        value={userGroupFilter}
                        onChange={(event) => {
                            setUserGroupFilter(event.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">
                            {t("Organizational.CustomerClub.Members.Group.All")}
                        </option>
                        {userGroups.map((group) => (
                            <option key={group.id} value={group.id}>
                                {group.name}
                            </option>
                        ))}
                    </select>
                </label>
                <p className="member-result-count" aria-live="polite">
                    {t("Organizational.CustomerClub.Members.ResultCount", {
                        count: formatCount(totalCount, locale),
                    })}
                </p>
            </div>

            <div className="member-table-wrap">
                {error ? (
                    <div className="member-empty" role="alert">
                        <span>{error}</span>
                        <DBButton
                            type="secondary"
                            outlined
                            onClick={() => fetchMembers(currentPage)}
                            caption={t("Organizational.CustomerClub.Members.Retry")}
                        />
                    </div>
                ) : (
                    <table className="member-table">
                        <caption className="visually-hidden">
                            {t("Organizational.CustomerClub.Members.Title")}
                        </caption>
                        <thead>
                            <tr>
                                <th scope="col">
                                    {t("Organizational.CustomerClub.Members.Col.Member")}
                                </th>
                                <th scope="col">
                                    {t("Organizational.CustomerClub.Members.Col.Joined")}
                                </th>
                                <th scope="col">
                                    {t("Organizational.CustomerClub.Members.Col.Group")}
                                </th>
                                <th scope="col" title={t("Organizational.CustomerClub.Members.Hint.Messages")}>
                                    {t("Organizational.CustomerClub.Members.Col.Messages")}
                                </th>
                                <th scope="col" title={t("Organizational.CustomerClub.Members.Hint.Ideas")}>
                                    {t("Organizational.CustomerClub.Members.Col.Ideas")}
                                </th>
                                <th scope="col" title={t("Organizational.CustomerClub.Members.Hint.Referrals")}>
                                    {t("Organizational.CustomerClub.Members.Col.Referrals")}
                                </th>
                                <th scope="col" title={t("Organizational.CustomerClub.Members.Hint.Coins")}>
                                    {t("Organizational.CustomerClub.Members.Col.Coins")}
                                </th>
                                <th scope="col" title={t("Organizational.CustomerClub.Members.Hint.Views")}>
                                    {t("Organizational.CustomerClub.Members.Col.Views")}
                                </th>
                                <th scope="col">
                                    <span className="visually-hidden">
                                        {t("Organizational.CustomerClub.Members.Col.Actions")}
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        <tbody aria-busy={loading}>
                            {loading ? (
                                <tr>
                                    <td colSpan={9}>
                                        <div className="member-loading" role="status">
                                            <Spinner animation="border" size="sm" />
                                            <span>
                                                {t(
                                                    "Organizational.CustomerClub.Members.Loading"
                                                )}
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            ) : showEmpty ? (
                                <tr>
                                    <td colSpan={9}>
                                        <div className="member-empty">
                                            <Profile2User size={28} aria-hidden="true" />
                                            <strong>
                                                {t(
                                                    "Organizational.CustomerClub.Members.Empty"
                                                )}
                                            </strong>
                                            <span>
                                                {t(
                                                    "Organizational.CustomerClub.Members.Empty.Hint"
                                                )}
                                            </span>
                                            <DBButton
                                                type="primary"
                                                onClick={showNewUserModal}
                                                caption={t(
                                                    "Organizational.CustomerClub.Members.Add"
                                                )}
                                            />
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                members.map((member) => (
                                    <CustomerClubTableRow
                                        key={member.id}
                                        data={member}
                                        setRefresh={setRefreshList}
                                        userGroups={userGroups}
                                        locale={locale}
                                        onOpen={setOpenMember}
                                    />
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {!loading && totalCount > PAGE_SIZE ? (
                <div className="member-pagination">
                    <DBPagination
                        current={currentPage}
                        total={totalCount}
                        pageSize={PAGE_SIZE}
                        onChange={(page) => setCurrentPage(page)}
                    />
                </div>
            ) : null}

            <Modal show={userModal} onHide={showNewUserModal} centered>
                <Modal.Body>
                    <NewUserModal
                        onHide={showNewUserModal}
                        changeRefreshValue={changeRefreshValue}
                        editMode={editMode}
                        itemId={itemId}
                    />
                </Modal.Body>
            </Modal>

            <MemberReportModal
                show={reportOpen}
                onHide={() => setReportOpen(false)}
                summary={summary}
                loading={summaryLoading}
                onExport={exportCsv}
                exportLoading={exportLoading}
                locale={locale}
            />

            <MemberDetailModal
                member={openMember}
                show={Boolean(openMember)}
                onHide={() => setOpenMember(null)}
                onOpenMessage={(id) => {
                    setFeedbackId(id);
                    setMessageModal(true);
                }}
                locale={locale}
            />

            <MessageContext.Provider
                value={{
                    messageModal,
                    setMessageModal,
                    feedbackId,
                    setFeedbackId,
                }}
            >
                <MessagesModal
                    userType="organ"
                    onBookmark={() => {}}
                    refreshMode={refreshMode}
                    setRefreshMode={setRefreshMode}
                />
            </MessageContext.Provider>
        </div>
    );
}
